import React, { useState, useEffect } from 'react';
import SVGIcon from '../SVGIcon/SVGIcon';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import './AuthModal.css';

export default function AuthModal({ isOpen, onClose, defaultMode = 'login', triggerNotification = () => {}, onLoginSuccess = () => {} }) {
  const [mode, setMode] = useState(defaultMode); // 'login' | 'signup' | 'forgot'
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Login fields
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Signup fields
  const [signupName, setSignupName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPhone, setSignupPhone] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupConfirmPassword, setSignupConfirmPassword] = useState('');

  // Forgot Password fields
  const [forgotEmail, setForgotEmail] = useState('');
  const [modalError, setModalError] = useState('');

  // Reset inputs when mode changes
  useEffect(() => {
    setMode(defaultMode);
    setModalError('');
  }, [defaultMode, isOpen]);

  useEffect(() => {
    setModalError('');
  }, [mode]);

  if (!isOpen) return null;

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setModalError('');
    if (!loginEmail || !loginPassword) {
      setModalError('Please fill in all fields.');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/signin`, { email: loginEmail, password: loginPassword });
      
      const data = response.data;
      
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      
      triggerNotification(data.message || `Logged in successfully!`);
      onLoginSuccess(data.user, data.token);
      onClose();
    } catch (err) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to authenticate.';
      setModalError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    setModalError('');
    if (!signupName || !signupEmail || !signupPhone || !signupPassword || !signupConfirmPassword) {
      setModalError('Please fill in all fields.');
      return;
    }
    if (signupPassword !== signupConfirmPassword) {
      setModalError('Passwords do not match.');
      return;
    }

    // Split name into first/last name for backend compatibility
    const nameParts = signupName.trim().split(/\s+/);
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || 'Farmer';

    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/signup`, {
        email: signupEmail,
        password: signupPassword,
        first_name: firstName,
        last_name: lastName,
        phone_number: signupPhone
      });

      const data = response.data;

      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));

      triggerNotification(data.message || `Account created successfully!`);
      onLoginSuccess(data.user, data.token);
      onClose();
    } catch (err) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to register account.';
      setModalError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSubmit = (e) => {
    e.preventDefault();
    setModalError('');
    if (!forgotEmail) {
      setModalError('Please enter your email.');
      return;
    }
    triggerNotification(`Password reset link sent to ${forgotEmail}!`);
    setMode('login');
  };

  return (
    <div className="auth-modal-overlay open" onClick={onClose}>
      <div className="auth-modal-card animate-scale-up" onClick={(e) => e.stopPropagation()}>
        
        {/* Close Button */}
        <button className="auth-modal-close-btn" onClick={onClose} aria-label="Close Auth Modal">
          <SVGIcon name="close" size={24} />
        </button>

        {/* Left Side: Curved Organic Illustration Panel */}
        <div className="auth-modal-illustration-panel">
          <div className="organic-clip-shape">
            <img 
              src="/banner/auth-banner.png" 
              alt="Orbifert Organic Growth Fertilizer Banner" 
              className="auth-illustration-img" 
            />
            <div className="illustration-overlay-text">
              <h3>Bumper Harvest</h3>
              <p>Nature's Goodness, Better Tomorrow.</p>
            </div>
          </div>
        </div>

        {/* Right Side: Auth Forms */}
        <div className="auth-modal-form-panel">
          
          {/* ---- LOGIN MODE ---- */}
          {mode === 'login' && (
            <div className="auth-form-container">
              <h2>Log in</h2>
              {modalError && <div className="auth-modal-error-banner">{modalError}</div>}
              <form onSubmit={handleLoginSubmit}>
                <div className="form-group">
                  <label htmlFor="loginEmail">Login, email or phone number</label>
                  <input
                    type="text"
                    id="loginEmail"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    placeholder="Enter email or phone number"
                    required
                  />
                </div>
                
                <div className="form-group password-group">
                  <div className="label-row">
                    <label htmlFor="loginPassword">Password</label>
                  </div>
                  <div className="input-with-icon">
                    <input
                      type={showLoginPassword ? 'text' : 'password'}
                      id="loginPassword"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      placeholder="Enter password"
                      required
                    />
                    <button 
                      type="button" 
                      className="password-toggle-btn"
                      onClick={() => setShowLoginPassword(!showLoginPassword)}
                      aria-label="Toggle Password Visibility"
                    >
                      <SVGIcon name={showLoginPassword ? "eye" : "eyeOff"} size={18} stroke={showLoginPassword ? "var(--primary)" : "#aaa"} />
                    </button>
                  </div>
                </div>

                <button type="submit" className="btn btn-primary auth-submit-btn" disabled={loading}>
                  {loading ? 'Logging in...' : 'Log in'}
                </button>
              </form>

              <div className="forgot-login-row">
                <button className="text-link-btn" onClick={() => setMode('forgot')}>
                  Forgot login or password?
                </button>
              </div>

              <div className="auth-divider-text">
                <span>or log in with</span>
              </div>

              {/* Social Logins */}
              <div className="social-logins-row">
                <button 
                  className="social-btn google-btn"
                  onClick={() => triggerNotification('Connecting to Google Account...')}
                >
                  <svg className="social-svg-logo" viewBox="0 0 24 24" width="20" height="20">
                    <path fill="#EA4335" d="M12.24 10.285V14.4h6.887c-.648 2.41-2.519 4.114-5.136 4.114A5.99 5.99 0 0 1 8 12.5a5.99 5.99 0 0 1 5.99-6.012c1.603 0 3.065.592 4.195 1.564l3.242-3.242C19.297 2.85 16.786 1.8 13.99 1.8 8.086 1.8 3.2 6.6 3.2 12.5s4.886 10.7 10.79 10.7c6.113 0 10.8-4.32 10.8-10.7 0-.742-.089-1.428-.242-2.215H12.24z"/>
                  </svg>
                </button>
                <button 
                  className="social-btn office-btn"
                  onClick={() => triggerNotification('Connecting to Microsoft Account...')}
                >
                  <svg className="social-svg-logo" viewBox="0 0 24 24" width="18" height="18">
                    <path fill="#F25022" d="M1 1h10v10H1z"/>
                    <path fill="#7FBA00" d="M13 1h10v10H13z"/>
                    <path fill="#00A4EF" d="M1 13h10v10H1z"/>
                    <path fill="#FFB900" d="M13 13h10v10H13z"/>
                  </svg>
                </button>
              </div>

              <div className="mode-toggle-footer">
                Don't have an account?{' '}
                <button className="auth-mode-switch" onClick={() => setMode('signup')}>
                  Sign up
                </button>
              </div>
            </div>
          )}

          {/* ---- SIGNUP MODE ---- */}
          {mode === 'signup' && (
            <div className="auth-form-container signup-container">
              <h2>Sign up</h2>
              {modalError && <div className="auth-modal-error-banner">{modalError}</div>}
              <form onSubmit={handleSignupSubmit}>
                <div className="signup-grid">
                  <div className="form-group">
                    <label htmlFor="signupName">Full Name</label>
                    <input
                      type="text"
                      id="signupName"
                      value={signupName}
                      onChange={(e) => setSignupName(e.target.value)}
                      placeholder="Enter your full name"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="signupEmail">Email Address</label>
                    <input
                      type="email"
                      id="signupEmail"
                      value={signupEmail}
                      onChange={(e) => setSignupEmail(e.target.value)}
                      placeholder="name@example.com"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="signupPhone">Phone Number</label>
                    <input
                      type="tel"
                      id="signupPhone"
                      value={signupPhone}
                      onChange={(e) => setSignupPhone(e.target.value.replace(/\D/g, ''))}
                      placeholder="Enter phone number"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="signupPassword">Password</label>
                    <div className="input-with-icon">
                      <input
                        type={showSignupPassword ? 'text' : 'password'}
                        id="signupPassword"
                        value={signupPassword}
                        onChange={(e) => setSignupPassword(e.target.value)}
                        placeholder="Create a strong password"
                        required
                      />
                      <button 
                        type="button" 
                        className="password-toggle-btn"
                        onClick={() => setShowSignupPassword(!showSignupPassword)}
                        aria-label="Toggle Password Visibility"
                      >
                        <SVGIcon name={showSignupPassword ? "eye" : "eyeOff"} size={18} stroke={showSignupPassword ? "var(--primary)" : "#aaa"} />
                      </button>
                    </div>
                  </div>

                  <div className="form-group confirm-password-group">
                    <label htmlFor="signupConfirmPassword">Confirm Password</label>
                    <div className="input-with-icon">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        id="signupConfirmPassword"
                        value={signupConfirmPassword}
                        onChange={(e) => setSignupConfirmPassword(e.target.value)}
                        placeholder="Confirm your password"
                        required
                      />
                      <button 
                        type="button" 
                        className="password-toggle-btn"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        aria-label="Toggle Password Visibility"
                      >
                        <SVGIcon name={showConfirmPassword ? "eye" : "eyeOff"} size={18} stroke={showConfirmPassword ? "var(--primary)" : "#aaa"} />
                      </button>
                    </div>
                  </div>
                </div>

                <button type="submit" className="btn btn-primary auth-submit-btn" disabled={loading}>
                  {loading ? 'Signing up...' : 'Sign up'}
                </button>
              </form>

              <div className="auth-divider-text">
                <span>or sign up with</span>
              </div>

              {/* Social Signups */}
              <div className="social-logins-row">
                <button 
                  className="social-btn google-btn"
                  onClick={() => triggerNotification('Connecting to Google Account...')}
                >
                  <svg className="social-svg-logo" viewBox="0 0 24 24" width="20" height="20">
                    <path fill="#EA4335" d="M12.24 10.285V14.4h6.887c-.648 2.41-2.519 4.114-5.136 4.114A5.99 5.99 0 0 1 8 12.5a5.99 5.99 0 0 1 5.99-6.012c1.603 0 3.065.592 4.195 1.564l3.242-3.242C19.297 2.85 16.786 1.8 13.99 1.8 8.086 1.8 3.2 6.6 3.2 12.5s4.886 10.7 10.79 10.7c6.113 0 10.8-4.32 10.8-10.7 0-.742-.089-1.428-.242-2.215H12.24z"/>
                  </svg>
                </button>
                <button 
                  className="social-btn office-btn"
                  onClick={() => triggerNotification('Connecting to Microsoft Account...')}
                >
                  <svg className="social-svg-logo" viewBox="0 0 24 24" width="18" height="18">
                    <path fill="#F25022" d="M1 1h10v10H1z"/>
                    <path fill="#7FBA00" d="M13 1h10v10H13z"/>
                    <path fill="#00A4EF" d="M1 13h10v10H1z"/>
                    <path fill="#FFB900" d="M13 13h10v10H13z"/>
                  </svg>
                </button>
              </div>

              <div className="mode-toggle-footer">
                Already have an account?{' '}
                <button className="auth-mode-switch" onClick={() => setMode('login')}>
                  Log in
                </button>
              </div>
            </div>
          )}

          {/* ---- FORGOT PASSWORD MODE ---- */}
          {mode === 'forgot' && (
            <div className="auth-form-container">
              <h2>Reset Password</h2>
              <p className="forgot-instructions">
                Enter your login email or username. We will send you a secure link to reset your password.
              </p>
              {modalError && <div className="auth-modal-error-banner">{modalError}</div>}
              <form onSubmit={handleForgotSubmit}>
                <div className="form-group">
                  <label htmlFor="forgotEmail">Email Address</label>
                  <input
                    type="email"
                    id="forgotEmail"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="name@example.com"
                    required
                  />
                </div>

                <button type="submit" className="btn btn-primary auth-submit-btn">
                  Send reset link
                </button>
              </form>

              <div className="mode-toggle-footer">
                Back to{' '}
                <button className="auth-mode-switch" onClick={() => setMode('login')}>
                  Log in
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
